/**
 * Mock category data for now, for static generation
 * Source navigation_categories in udemy/browse/context_processors.py
 * need to provide slug url instead of absolute url.
 */
import {i18nPathToBundleMap, i18nPreferred} from 'next.config.i18n';
import {CategoryPageType} from 'src/features/category/types';
import {CourseCategory, useGetCategoryQuery} from 'src/generated/graphql';
import {getRuntimeConfig} from 'src/pages/api/config.api';

import {CategoryNotFoundError} from './errors';
import {parseCategoryData} from './parse-category-data';

/** TODO: type the category context */
// eslint-disable-next-line
export const shouldRedirect = (categoryContext: any) =>
    !!categoryContext?.redirect_context?.destination;

export const getCategoryPageObject = (
    categorySlug: string,
    categoryData: CourseCategory,
    categoryType: CategoryPageType,
) => ({
    id: typeof categoryData.id === 'number' ? categoryData.id : Number(categoryData.id),
    title: categoryData.name,
    title_cleaned: categorySlug,
    type: categoryType,
});

export async function fetchCategoryDataFromGql(slug?: string, locale?: string) {
    if (!slug) {
        return null;
    }
    return useGetCategoryQuery
        .fetcher(
            {id: slug},
            {
                'Accept-Language': locale as string,
            },
        )()
        .then((response) => {
            return response?.courseCategory;
        })
        .catch((e) => {
            // eslint-disable-next-line no-console
            console.error(
                `Error fetching category info data from graphql with slug: ${slug}. Error: ${JSON.stringify(
                    e,
                )}`,
            );
            return null;
        });
}

export async function getCategoryData(rawCategorySlug: string, locale?: string) {
    const categorySlug = rawCategorySlug.toLowerCase();
    const lang = locale || i18nPreferred[0];
    const langPrefix = lang === 'en' ? '' : `${lang}/`;
    const fullLocale = i18nPathToBundleMap[lang as keyof typeof i18nPathToBundleMap]
        .split('_')
        .join('-');

    const url = `${getRuntimeConfig('MONOLITH_URL')}/api-2.0/browse/${langPrefix}category-context/${categorySlug}/`;

    let categoryContextData;
    try {
        const response = await fetch(url, {
            credentials: 'include',
            headers: {
                'Accept-Language': fullLocale,
            },
        });

        if (response.status === 404) {
            throw new CategoryNotFoundError(
                `CategoryContextData not found with slug: ${rawCategorySlug} by using ${url}`,
            );
        } else if (response.status >= 400) {
            throw new Error(`Request failed with status code ${response.status}`);
        }
        categoryContextData = await response.json();
    } catch (error) {
        if (error instanceof CategoryNotFoundError) {
            throw error;
        }
        // eslint-disable-next-line no-console
        console.error(
            `Error fetching category context data from monolith with slug: ${rawCategorySlug} by using ${url}. Error: ${JSON.stringify(
                error,
            )}`,
        );
        throw error;
    }

    // If we need to redirect, we should return it directly, don't need to call gql for additional topic data
    if (shouldRedirect(categoryContextData)) {
        return categoryContextData;
    }

    const categoryData = await fetchCategoryDataFromGql(categorySlug, locale);
    const isCategoryDataValid = !!categoryData && Object.keys(categoryData).length > 0;
    /**
     * CategoryData should not be empty (means that not found), if the first call is successful because they are both using
     * the same data source under the hood. So if the first call is successful, the second call should be successful too.
     * Otherwise, something is wrong.
     */

    return {
        ...categoryContextData,
        ...categoryData,
        category: isCategoryDataValid
            ? parseCategoryData(
                  categoryData,
                  `CategoryData returned unexpected data with slug: ${rawCategorySlug} by using useGetCategoryQuery`,
              )
            : undefined,
    };
}
